// Смена источника чтения курса (`course.readId`: появился черновик, его слили
// или отменили) меняет id уроков и блоков, но это ТЕ ЖЕ сущности. Хук переводит
// выделение редактора на них по живой идентичности (`originId ?? id`).
import { useEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import type { RootState } from '@/store'
import { api } from '@/store/api'
import { useAppDispatch } from '@/store/hooks'
import type { CourseWithoutBlocks } from '@/types/course'
import { draftSourceSettled, remapSelection } from '@/features/editor/editorSlice'
import type { IdPair } from '@/features/editor/lib/idAliases'
import { expectedReadIdOf } from '@/features/moderation/lib/expectedReadId'
import {
  blockPairs,
  cachedLessonsBlockPairs,
  idPairs,
  lessonsToLoad,
  type AppDispatch,
} from './readSourceBlockPairs'

const remapReadSource = async (
  prev: CourseWithoutBlocks,
  next: CourseWithoutBlocks,
  getState: () => RootState,
  dispatch: AppDispatch,
  isAlive: () => boolean,
) => {
  const lessons = idPairs(prev.lessons, next.lessons)
  const oldLessonId = getState().editor.selectedLessonId
  // Кеши блоков уроков НОВОГО источника могли остаться с прошлой сессии (живой урок до
  // черновика) — сбрасываются здесь, где их id уже известны. Уроки старого источника не
  // трогаются: на них ещё подписана панель, а удалённый черновик ответил бы 404 с тостом.
  // Уроки, которые переезд перечитает сам, не трогаются тоже (`lessonsToLoad`).
  const loading = lessonsToLoad(getState, lessons, oldLessonId)
  const tags = next.lessons
    .filter((l) => !loading.has(l.id))
    .map((l) => ({ type: 'Block' as const, id: `lesson-${l.id}` }))
  dispatch(api.util.invalidateTags(tags))
  const blocks: IdPair[] = []
  if (oldLessonId !== null) {
    const found = lessons.find(([from]) => from === oldLessonId)
    const newLessonId = found ? found[1] : oldLessonId
    blocks.push(...(await blockPairs(getState, dispatch, oldLessonId, newLessonId)))
  }
  blocks.push(...(await cachedLessonsBlockPairs(getState, dispatch, lessons, oldLessonId)))
  // Хук размонтирован (редактор закрыт) — выделение уже чужой сессии.
  if (isAlive()) dispatch(remapSelection({ lessons, blocks }))
}

/**
 * Переезды идут СТРОГО по очереди: каждый читает выделение уже после того, как
 * применился предыдущий. Параллельные переезды (42 → 99 и сразу 99 → 42) читали бы
 * одно и то же до-переездное выделение, а поздний ответ первого перекрыл бы второй —
 * выделение осталось бы на id ушедшего источника, и «Сохранить» писало бы в него.
 */
export const useReadSourceRemap = (course: CourseWithoutBlocks | undefined): void => {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const prevRef = useRef(course)
  const queueRef = useRef<Promise<void>>(Promise.resolve())
  const aliveRef = useRef(true)
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
    }
  }, [])
  useEffect(() => {
    const prev = prevRef.current
    if (course === undefined) return
    prevRef.current = course
    if (!prev || prev.id !== course.id || prev.readId === course.readId) return
    const isAlive = () => aliveRef.current
    // `catch` — чтобы один упавший переезд не остановил очередь навсегда.
    // Переезд закончен (или упал) — источник осел: пакетные записи, ждавшие его
    // (`editor.draftSourceSettling`), теперь соберут тела уже из нового источника. Но
    // только если это ТОТ источник, что назвал кеш `/moderation`: пока переезжали на живой
    // (черновик отменён), запись автора могла завести новый черновик — признак ждёт
    // следующего переезда (или сторожа).
    queueRef.current = queueRef.current
      .then(() =>
        isAlive() ? remapReadSource(prev, course, store.getState, dispatch, isAlive) : undefined,
      )
      .catch(() => undefined)
      .then(() => {
        if (course.readId === expectedReadIdOf(store.getState(), course.id))
          dispatch(draftSourceSettled(course.id))
      })
  }, [course, store, dispatch])
}
